/**
 * Sayfa izinleri (RBAC) yardımcıları.
 *
 * Yeni model: page_permissions = { "/it/users/create": ["read","edit","add","delete"], "*": [...] }
 * "*" joker anahtarı tüm sayfalarda o aksiyonlara izin verir (admin rolü).
 *
 * Geçiş dönemi: page_permissions boşsa eski permitted_pages listesine düşülür;
 * migrasyon her sayfaya tüm aksiyonları verdiğinden legacy eşleşme tüm
 * aksiyonlar için geçerli sayılır.
 */

export type PermAction = 'read' | 'edit' | 'add' | 'delete';
export type PagePermissions = Record<string, string[]>;

export const PERM_ACTIONS: PermAction[] = ['read', 'edit', 'add', 'delete'];
export const PERM_WILDCARD = '*';

/**
 * Sayfa izni verilmiş olsa bile YALNIZCA admin rolünün erişebildiği sayfalar.
 * (Backend uçları zaten admin-only; bu liste sayfayı ve menü öğesini de gizler.)
 */
/**
 * Merkezî PageGuard'ın izin aramadığı, giriş yapmış her kullanıcıya açık sayfalar.
 * Bu yollar izin verme ekranında da listelenmez (verilmeleri anlamsızdır).
 */
export const GUARD_EXEMPT_PATHS = new Set<string>([
  '/',
  '/login',
  '/home',
  '/profile',
  '/tasks',
  '/form-preview',
]);

export const ADMIN_ONLY_PATHS = new Set<string>([
  '/it/users/grant_permission',
  '/it/users/create',
  '/it/users/list',
  '/it/users/edit/:id',
]);

export function isPathAllowedForRole(path: string, role: string | null | undefined): boolean {
  if (role === 'admin') return true;
  if (ADMIN_ONLY_PATHS.has(path)) return false;
  // Dinamik yollar: /it/users/edit/5 -> /it/users/edit/:id
  const { normalizedPath } = normalizePathname(path);
  return !ADMIN_ONLY_PATHS.has(normalizedPath);
}

/** PageGuard'daki dinamik id normalizasyonunun ortak hali */
export function normalizePathname(pathname: string): {
  normalizedPath: string;
  basePathForDynamic: string | null;
} {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments[segments.length - 1] || '';
  const isNumericId = /^\d+$/.test(last);
  const isUuidLike = /^[0-9a-fA-F-]{8,}$/.test(last);
  const isAccId = /^b\.[0-9a-fA-F-]{8,}$/.test(last);
  const looksLikeDynamicId = isNumericId || isUuidLike || isAccId;

  const normalizedSegments = [...segments];
  if (segments.length > 1 && looksLikeDynamicId) {
    normalizedSegments[normalizedSegments.length - 1] = ':id';
  }
  return {
    normalizedPath: '/' + normalizedSegments.join('/'),
    basePathForDynamic:
      looksLikeDynamicId && segments.length > 1 ? '/' + segments.slice(0, -1).join('/') : null,
  };
}

/**
 * Paths moved under /core/general. Grants on the old address still open the new page,
 * and a grant on the new address still passes the redirect route.
 */
const MOVED_PAGE_ALIASES: Record<string, string> = {
  '/core/pdf-to-react': '/core/general/pdf-to-react',
  '/core/pdf-to-svg': '/core/general/pdf-to-svg',
  '/core/pdf-text-extract': '/core/general/pdf-text-extract',
  '/core/exif-reader': '/core/general/exif-reader',
};

function withMovedAliases(path: string): string[] {
  const extra = new Set<string>([path]);
  const movedTo = MOVED_PAGE_ALIASES[path];
  if (movedTo) extra.add(movedTo);
  for (const [oldPath, newPath] of Object.entries(MOVED_PAGE_ALIASES)) {
    if (newPath === path) extra.add(oldPath);
  }
  return Array.from(extra);
}

/** Tek yol + aksiyon kontrolü (joker dahil, normalizasyon YOK) */
export function hasAction(
  perms: PagePermissions | null | undefined,
  path: string,
  action: PermAction
): boolean {
  if (!perms) return false;
  const wildcard = perms[PERM_WILDCARD];
  if (Array.isArray(wildcard) && wildcard.includes(action)) return true;
  const actions = perms[path];
  return Array.isArray(actions) && actions.includes(action);
}

/**
 * Sayfa + aksiyon kontrolü: tam yol, normalize yol ve dinamik base yolu dener.
 * page_permissions boşsa legacy permitted_pages listesine düşer.
 */
export function canActOnPage(
  perms: PagePermissions | null | undefined,
  legacyPages: string[],
  pathname: string,
  action: PermAction
): boolean {
  const { normalizedPath, basePathForDynamic } = normalizePathname(pathname);
  const candidates = [pathname, normalizedPath, ...(basePathForDynamic ? [basePathForDynamic] : [])]
    .flatMap(withMovedAliases);

  if (perms && Object.keys(perms).length > 0) {
    return candidates.some(p => hasAction(perms, p, action));
  }
  // Geçiş dönemi: legacy listedeki sayfa tüm aksiyonlara sahip kabul edilir
  return candidates.some(p => legacyPages.includes(p));
}
